import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ImageUp, Trash2, X } from 'lucide-react';
import {
  OLT_IMAGE_CONTENT_TYPES,
  OLT_IMAGE_MAX_BYTES,
  type Olt,
} from '@simpleacs/shared';
import { Button } from '../../components/ui';
import { useSession } from '../../lib/session';
import { ChassisArtwork, artForOlt } from './ChassisArt';

/**
 * The picture at the top of the Stats card.
 *
 * The platform this replaces puts a photograph of the chassis above its
 * figures, and that one detail is most of what makes an OLT page feel like it
 * is about a specific box in a specific rack rather than about a row that
 * happens to be selected. So: a thumbnail, always — an operator's uploaded
 * photograph when there is one, a drawing of the model's family when there is
 * not — and the full image on a click.
 *
 * Two things it deliberately does not do. It adds no prose to the card, which
 * was just rewritten to be label-left, figure-right with everything
 * explanatory behind Details; the one sentence about what a drawing IS lives
 * next to the full image, where somebody is actually looking at it. And it
 * does not re-fetch: the Stats card polls every thirty seconds, so the image
 * URL carries the upload's own timestamp and is therefore immutable per
 * version — the browser fetches these bytes once, and the API's ETag catches
 * anything that slips past that.
 */

/** The `accept` attribute, from the same list the API enforces on the bytes. */
const ACCEPT = OLT_IMAGE_CONTENT_TYPES.join(',');
const MAX_MB = OLT_IMAGE_MAX_BYTES / 1024 / 1024;

/**
 * Re-read the OLT so the new picture is on screen before the poll comes round.
 *
 * By shape rather than by id, and deliberately: the detail query is keyed on
 * whatever was in the URL, which may be the OLT's id OR its human-friendly
 * number (`/olts/1` resolves to the same chassis), so `['olt', olt.id]` misses
 * it exactly half the time. `['olt', <anything>]` with nothing after it is the
 * OLT record itself and only ever has one instance mounted — the per-tab
 * queries all carry a third element and are left alone.
 */
async function refreshOlt(queryClient: ReturnType<typeof useQueryClient>): Promise<void> {
  await queryClient.invalidateQueries({
    predicate: (query) => query.queryKey[0] === 'olt' && query.queryKey.length === 2,
  });
}

function imageUrl(olt: Olt): string | null {
  if (!olt.image) return null;
  // `v` is the cache key, not a cache-buster: same picture, same URL, one
  // fetch — and a new URL exactly when the picture changed.
  return `/api/olts/${encodeURIComponent(olt.id)}/image?v=${olt.image.uploadedAt}`;
}

export function OltImageBlock({ olt }: { olt: Olt }) {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const canManage = can('olt.manage');
  const src = imageUrl(olt);
  const art = artForOlt(olt);

  async function upload(file: File) {
    setError(null);
    // Met at the keyboard as well as at the API. The API is the one that
    // counts — it checks the bytes, not the picker — but a 4 MB round trip
    // that was always going to be refused is a slow way to learn that.
    if (file.size > OLT_IMAGE_MAX_BYTES) {
      setError(
        `That image is ${Math.round((file.size / 1024 / 1024) * 10) / 10} MB and the limit is ${MAX_MB} MB.`,
      );
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`/api/olts/${encodeURIComponent(olt.id)}/image`, {
        method: 'PUT',
        credentials: 'same-origin',
        // The bytes, raw. What KIND of image it is, the API decides from the
        // file's own contents — this header is a transport detail and is not
        // a claim about the picture.
        headers: { 'Content-Type': 'application/octet-stream' },
        body: file,
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
        throw new Error(body?.error?.message ?? `Upload failed (${res.status})`);
      }
      await refreshOlt(queryClient);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not upload that image.');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setError(null);
    setBusy(true);
    try {
      const res = await fetch(`/api/olts/${encodeURIComponent(olt.id)}/image`, {
        method: 'DELETE',
        credentials: 'same-origin',
      });
      if (!res.ok) throw new Error(`Could not remove the image (${res.status})`);
      await refreshOlt(queryClient);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove that image.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="border-b border-line px-5 py-4">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(true)}
        className="block w-full cursor-zoom-in overflow-hidden rounded-lg border border-line bg-surface-2 transition-colors hover:border-line-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)] motion-reduce:transition-none"
        aria-label={
          src
            ? `Open the full photograph of ${olt.name}`
            : `Open the full ${art.label} drawing — an approximation, not a photograph of this chassis`
        }
      >
        <span className="relative block">
          {src ? (
            <img
              src={src}
              alt={`Photograph of ${olt.name}`}
              className="h-32 w-full bg-surface-2 object-contain"
            />
          ) : (
            <ChassisArtwork olt={olt} className="h-32 w-full" />
          )}
          {!src && (
            // Four letters rather than a sentence. Without it the card is
            // showing a picture of a chassis that is not this chassis and not
            // saying so; with it, nobody mistakes the drawing for a photo.
            <span className="absolute left-2 top-2 rounded bg-surface/85 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-faint">
              drawing
            </span>
          )}
        </span>
      </button>

      {canManage && (
        <div className="mt-2 flex items-center justify-end gap-2">
          <input
            ref={fileRef}
            type="file"
            accept={ACCEPT}
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void upload(file);
              e.target.value = '';
            }}
          />
          {src && (
            <Button size="sm" variant="ghost" icon={<Trash2 className="size-3.5" />} disabled={busy} onClick={() => void remove()}>
              Remove
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            icon={<ImageUp className="size-3.5" />}
            loading={busy}
            onClick={() => fileRef.current?.click()}
          >
            {src ? 'Replace photo' : 'Add photo'}
          </Button>
        </div>
      )}

      {error && <p className="mt-2 text-xs text-danger">{error}</p>}

      {open && (
        <Lightbox
          title={src ? olt.name : art.label}
          caption={src ? null : art.basis}
          onClose={() => {
            setOpen(false);
            // Back to the thumbnail that opened it — otherwise a keyboard
            // user is returned to the top of the document and has to walk the
            // whole page again to get back to where they were.
            triggerRef.current?.focus();
          }}
        >
          {src ? (
            <img
              src={src}
              alt={`Photograph of ${olt.name}`}
              className="max-h-[70vh] w-full rounded-lg object-contain"
            />
          ) : (
            <ChassisArtwork olt={olt} labels className="max-h-[70vh] w-full" />
          )}
        </Lightbox>
      )}
    </div>
  );
}

/**
 * The full image, over the page.
 *
 * Hand-built rather than pulled in: `components/ui` has no modal primitive and
 * every dialog in this app is the same handful of elements — a fixed layer, a
 * click-through backdrop, a panel — so this follows that shape instead of
 * adding a dialog library for one lightbox.
 *
 * What it does not skip: Escape closes it wherever focus happens to be (on the
 * document, not on one input, which is how a dialog ends up dismissable only
 * while a textarea has focus); focus moves into the dialog on open and is
 * returned to the opener on close; Tab cycles inside it rather than walking
 * the page behind; and the fade is a `motion-reduce` no-op for anybody who has
 * asked their system for less movement.
 */
function Lightbox({
  title,
  caption,
  onClose,
  children,
}: {
  title: string;
  caption: string | null;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    closeRef.current?.focus();
    // Two frames of nothing, then the opacity flips — which is what makes the
    // transition run at all rather than being the element's initial state.
    const id = requestAnimationFrame(() => setShown(true));
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const focusable = panelRef.current?.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable || focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      cancelAnimationFrame(id);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return createPortal(
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-150 motion-reduce:transition-none sm:p-8 ${
        shown ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <div className="absolute inset-0 bg-black/75" onClick={onClose} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="relative w-full max-w-4xl rounded-xl border border-line bg-surface shadow-2xl"
      >
        <header className="flex items-center justify-between gap-3 border-b border-line px-5 py-3">
          <h2 className="text-sm font-semibold text-ink">{title}</h2>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1.5 text-muted transition-colors hover:bg-surface-2 hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)] motion-reduce:transition-none"
          >
            <X className="size-4" />
          </button>
        </header>
        <div className="p-5">{children}</div>
        {caption && (
          // The honesty line, and the only place it belongs: somebody is
          // looking at the full drawing, so this is where "that is not your
          // chassis, and here is what it IS" costs nothing and settles it.
          <p className="border-t border-line px-5 py-3 text-xs leading-relaxed text-muted">
            An approximation, not a photograph. {caption}
          </p>
        )}
      </div>
    </div>,
    document.body,
  );
}
